import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import { X } from 'lucide-react';
import { useMemo } from 'react';

import type { ClassSearchDetails } from '~entities/classes/modal/class-search';
import type {
  PrivateLessonRecurrenceFrequency,
  SeriesOccurrencePreview,
} from '~entities/private-lessons/api/private-lessons';

import {
  EnrollioTable,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { formatTrialDate } from '~shared/helpers/format-trial-datetime';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import { Calendar } from '~shared/shadcn/ui/calendar';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

import {
  countSeriesOccurrences,
  formatDaysOfWeekLabel,
  getAllowedClassDays,
  getAllowedDayIndexes,
  isBookablePrivateLessonDate,
  parseOfferingDate,
  toDateKey,
} from './private-lesson-booking-helpers';
import { PrivateLessonDatePicker } from './private-lesson-date-picker';

export interface SeriesRecurrenceFormState {
  frequency: PrivateLessonRecurrenceFrequency;
  startDate: string;
  endDate: string;
  occurrenceCount: string;
  endMode: 'date' | 'count';
  daysOfWeek: Array<string>;
  customDates: Array<string>;
}

interface PrivateLessonRecurrenceFieldsProps {
  offering: ClassSearchDetails | null;
  value: SeriesRecurrenceFormState;
  onChange: (value: SeriesRecurrenceFormState) => void;
}

export const createDefaultRecurrenceState = (
  offering: ClassSearchDetails | null,
): SeriesRecurrenceFormState => {
  const allowedDays = getAllowedClassDays(offering);
  const startDate =
    parseOfferingDate(offering?.startDate)?.toISOString().slice(0, 10) ??
    toDateKey(new Date());
  const endDate =
    parseOfferingDate(offering?.endDate)?.toISOString().slice(0, 10) ?? '';
  const daysOfWeek = allowedDays[0] ? [allowedDays[0]] : ['Thursday'];

  const projectedCount = countSeriesOccurrences({
    frequency: 'biweekly',
    startDate,
    endDate,
    daysOfWeek,
    customDates: [],
    offering,
  });

  return {
    frequency: 'biweekly',
    startDate,
    endDate,
    occurrenceCount: String(Math.max(projectedCount, 1)),
    endMode: 'date',
    daysOfWeek,
    customDates: [],
  };
};

export const PrivateLessonRecurrenceFields = ({
  offering,
  value,
  onChange,
}: PrivateLessonRecurrenceFieldsProps) => {
  const allowedDays = getAllowedClassDays(offering);

  const resolveEffectiveEndDate = (state: SeriesRecurrenceFormState) =>
    state.endMode === 'date'
      ? state.endDate
      : (parseOfferingDate(offering?.endDate)?.toISOString().slice(0, 10) ??
        '');

  const computeProjectedCount = (state: SeriesRecurrenceFormState) =>
    countSeriesOccurrences({
      frequency: state.frequency,
      startDate: state.startDate,
      endDate: resolveEffectiveEndDate(state),
      daysOfWeek: state.daysOfWeek,
      customDates: state.customDates,
      offering,
    });

  const projectedLessonCount = computeProjectedCount(value);

  const update = (patch: Partial<SeriesRecurrenceFormState>) => {
    const next = { ...value, ...patch };
    if (next.frequency !== 'custom' && !('occurrenceCount' in patch)) {
      next.occurrenceCount = String(Math.max(computeProjectedCount(next), 1));
    }
    onChange(next);
  };

  const toggleDay = (day: string) => {
    const selected = value.daysOfWeek.includes(day)
      ? value.daysOfWeek.filter((d) => d !== day)
      : [...value.daysOfWeek, day];

    // Keep at least one day selected.
    if (selected.length === 0) {
      return;
    }

    // Preserve offering day order.
    const ordered = allowedDays.filter((d) => selected.includes(d));
    update({ daysOfWeek: ordered.length > 0 ? ordered : selected });
  };

  return (
    <div className='space-y-4 rounded-md border p-4'>
      <div className='space-y-2'>
        <Label>Repeat</Label>
        <Select
          value={value.frequency}
          onValueChange={(next) => {
            update({
              frequency: next as PrivateLessonRecurrenceFrequency,
            });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder='Select frequency' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='weekly'>Every week</SelectItem>
            <SelectItem value='biweekly'>Every 2 weeks</SelectItem>
            <SelectItem value='monthly'>Every month</SelectItem>
            <SelectItem value='custom'>Custom dates</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {value.frequency !== 'custom' && (
        <>
          <div className='space-y-2'>
            <Label>Days of week</Label>
            <div className='flex flex-wrap gap-2'>
              {allowedDays.map((day) => {
                const checked = value.daysOfWeek.includes(day);
                return (
                  <label
                    key={day}
                    className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm ${
                      checked ? 'border-primary bg-primary/5' : ''
                    }`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => {
                        toggleDay(day);
                      }}
                    />
                    {day.slice(0, 3)}
                  </label>
                );
              })}
            </div>
            <p className='text-xs text-muted-foreground'>
              Select one or more offering days. Same time applies to each.
            </p>
          </div>

          <PrivateLessonDatePicker
            offering={offering}
            date={value.startDate}
            onDateChange={(startDate) => {
              update({ startDate });
            }}
            label='Start date'
          />

          <div className='space-y-2'>
            <Label>End condition</Label>
            <Select
              value={value.endMode}
              onValueChange={(endMode) => {
                update({ endMode: endMode as 'date' | 'count' });
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='count'>After number of lessons</SelectItem>
                <SelectItem value='date'>On end date</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {value.endMode === 'count' ? (
            <div className='space-y-2'>
              <Label>Number of lessons</Label>
              <Input
                type='number'
                min={1}
                max={Math.max(projectedLessonCount, 1)}
                value={value.occurrenceCount}
                onChange={(event) => {
                  update({ occurrenceCount: event.target.value });
                }}
              />
              <p className='text-xs text-muted-foreground'>
                Up to {projectedLessonCount} lessons fit this repeat, days, and
                offering dates.
              </p>
            </div>
          ) : (
            <div className='space-y-2'>
              <Label>End date</Label>
              <Input
                type='date'
                value={value.endDate}
                min={value.startDate}
                max={parseOfferingDate(offering?.endDate)
                  ?.toISOString()
                  .slice(0, 10)}
                onChange={(event) => {
                  update({ endDate: event.target.value });
                }}
              />
            </div>
          )}

          <div className='rounded-md bg-muted/50 px-3 py-2 text-sm'>
            <span className='font-medium'>{projectedLessonCount}</span>{' '}
            {projectedLessonCount === 1 ? 'lesson' : 'lessons'} will be
            generated for{' '}
            <span className='font-medium'>
              {formatDaysOfWeekLabel(value.daysOfWeek)}
            </span>
            {value.frequency === 'weekly' && ' every week'}
            {value.frequency === 'biweekly' && ' every 2 weeks'}
            {value.frequency === 'monthly' && ' every month'}
            {value.endMode === 'date' && value.endDate
              ? ` through ${value.endDate}`
              : ''}
            .
          </div>
        </>
      )}

      {value.frequency === 'custom' && (
        <CustomDatesCalendar
          offering={offering}
          selectedDates={value.customDates}
          onChange={(customDates) => {
            update({ customDates });
          }}
        />
      )}
    </div>
  );
};

const formatCustomDateLabel = (dateKey: string) => {
  const parsed = new Date(`${dateKey.split('T')[0]}T12:00:00`);
  return Number.isNaN(parsed.getTime())
    ? dateKey
    : format(parsed, 'MMM d, yyyy');
};

const CustomDatesCalendar = ({
  offering,
  selectedDates,
  onChange,
}: {
  offering: ClassSearchDetails | null;
  selectedDates: Array<string>;
  onChange: (dates: Array<string>) => void;
}) => {
  const allowedDays = getAllowedClassDays(offering);
  const allowedDayIndexes = getAllowedDayIndexes(offering);
  const windowStart = parseOfferingDate(offering?.startDate);
  const windowEnd = parseOfferingDate(offering?.endDate);
  const selectedDateSet = new Set(selectedDates);
  const selectedModifiers = selectedDates.map(
    (dateKey) => new Date(`${dateKey.split('T')[0]}T12:00:00`),
  );

  const addDate = (nextDate: Date) => {
    const dateKey = toDateKey(nextDate);
    if (selectedDateSet.has(dateKey)) return;
    onChange([...selectedDates, dateKey].sort());
  };

  const removeDate = (dateKey: string) => {
    onChange(selectedDates.filter((value) => value !== dateKey));
  };

  return (
    <div className='space-y-3'>
      <div className='space-y-2'>
        <Label>Custom dates</Label>
        <p className='text-xs text-muted-foreground'>
          Pick offering days to add them to this series. Already added dates are
          disabled.
        </p>
        <div className='w-fit rounded-md border'>
          <Calendar
            mode='single'
            selected={undefined}
            modifiers={{ added: selectedModifiers }}
            modifiersClassNames={{
              added:
                'bg-primary/15 text-primary hover:bg-primary/15 hover:text-primary',
            }}
            defaultMonth={windowStart ?? new Date()}
            onSelect={(nextDate) => {
              if (!nextDate) return;
              addDate(nextDate);
            }}
            disabled={(candidate) =>
              selectedDateSet.has(toDateKey(candidate)) ||
              !isBookablePrivateLessonDate(
                candidate,
                offering,
                allowedDayIndexes,
                windowStart,
                windowEnd,
              )
            }
          />
        </div>
        {allowedDays.length > 0 && (
          <p className='text-xs text-muted-foreground'>
            Available on: {allowedDays.join(', ')}
          </p>
        )}
      </div>

      <div className='space-y-2'>
        <Label>
          {selectedDates.length === 0
            ? 'No dates added'
            : `${selectedDates.length} ${selectedDates.length === 1 ? 'date' : 'dates'} selected`}
        </Label>
        {selectedDates.length > 0 && (
          <div className='flex flex-wrap gap-2'>
            {selectedDates.map((dateKey) => (
              <Button
                key={dateKey}
                type='button'
                variant='secondary'
                size='sm'
                className='h-8 gap-1'
                onClick={() => {
                  removeDate(dateKey);
                }}
              >
                {formatCustomDateLabel(dateKey)}
                <X className='size-3.5' />
                <span className='sr-only'>Remove {dateKey}</span>
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface PrivateLessonSeriesPreviewProps {
  occurrences: Array<SeriesOccurrencePreview>;
  summary: { total: number; available: number; conflicts: number };
  timezone?: string;
  recurrenceLabel?: string;
}

const statusVariant = (
  status: SeriesOccurrencePreview['status'],
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (status) {
    case 'available':
      return 'default';
    case 'conflict':
      return 'destructive';
    default:
      return 'secondary';
  }
};

export const PrivateLessonSeriesPreview = ({
  occurrences,
  summary,
  timezone,
  recurrenceLabel,
}: PrivateLessonSeriesPreviewProps) => {
  const columns = useMemo(() => {
    const helper = createColumnHelper<SeriesOccurrencePreview>();
    return [
      helper.accessor('startTime', {
        header: 'Date',
        cell: ({ row }) =>
          formatTrialDate(row.original.startTime, timezone, 'MMM d, yyyy') ??
          row.original.date,
      }),
      helper.accessor('status', {
        header: 'Status',
        cell: ({ row }) => (
          <div>
            <Badge variant={statusVariant(row.original.status)}>
              {row.original.status}
            </Badge>
            {row.original.conflictReason ? (
              <p className='mt-1 text-xs text-muted-foreground'>
                {row.original.conflictReason}
              </p>
            ) : null}
          </div>
        ),
      }),
    ];
  }, [timezone]);

  return (
    <div className='space-y-3'>
      {recurrenceLabel && (
        <p className='text-sm text-muted-foreground'>{recurrenceLabel}</p>
      )}
      <div className='flex flex-wrap gap-2 text-sm'>
        <Badge variant='outline'>{summary.total} total</Badge>
        <Badge>{summary.available} available</Badge>
        {summary.conflicts > 0 && (
          <Badge variant='destructive'>{summary.conflicts} conflicts</Badge>
        )}
      </div>
      <EnrollioTable
        totalLabel='Series occurrences'
        filtersLayout='inline'
        filterHeader={(table) => (
          <EnrollioTableSearch table={table} placeholder='Search dates...' />
        )}
        data={occurrences}
        columns={columns}
        getRowId={(row) => row.startTime}
        paginationVariant='compact'
        emptyContent='No occurrences.'
        ariaLabel='Private lesson series occurrences'
      />
    </div>
  );
};

interface SeriesOutcomeEntry {
  startTime: string;
  reason: string;
}

interface PrivateLessonSeriesResultProps {
  booked: number;
  skipped: Array<SeriesOutcomeEntry>;
  failed: Array<SeriesOutcomeEntry>;
  canRetry: boolean;
  timezone?: string;
}

export const PrivateLessonSeriesResult = ({
  booked,
  skipped,
  failed,
  canRetry,
  timezone,
}: PrivateLessonSeriesResultProps) => {
  const failedColumns = useMemo(() => {
    const helper = createColumnHelper<SeriesOutcomeEntry>();
    return [
      helper.accessor('startTime', {
        header: 'Failed date',
        cell: (info) =>
          formatTrialDate(info.getValue(), timezone, 'MMM d, yyyy · h:mm a') ??
          info.getValue(),
      }),
      helper.accessor('reason', { header: 'Reason' }),
    ];
  }, [timezone]);

  return (
    <div className='space-y-3'>
      <div className='flex flex-wrap gap-2 text-sm'>
        <Badge>{booked} booked</Badge>
        {skipped.length > 0 && (
          <Badge variant='secondary'>{skipped.length} skipped</Badge>
        )}
        {failed.length > 0 && (
          <Badge variant='destructive'>{failed.length} failed</Badge>
        )}
      </div>

      {failed.length > 0 && (
        <>
          <EnrollioTable
            totalLabel='Failed lessons'
            filtersLayout='inline'
            filterHeader={(table) => (
              <EnrollioTableSearch
                table={table}
                placeholder='Search failures...'
              />
            )}
            data={failed}
            columns={failedColumns}
            getRowId={(row) => row.startTime}
            paginationVariant='compact'
            emptyContent='No failed lessons.'
            ariaLabel='Failed private lessons'
          />
          <p className='text-xs text-muted-foreground'>
            {canRetry
              ? 'Retrying books these dates into the same series. Lessons that already booked are detected as conflicts and skipped.'
              : 'These dates cannot be booked as scheduled, so retrying would fail the same way. Adjust the dates or times and book again.'}
          </p>
        </>
      )}
    </div>
  );
};
